import { AutomationType } from '../../types/enums/AutomationType';
import { DeployStatusForTable } from '../../types/enums/DeployStatusForTable';
import { useSweepApi } from '../sweep';
import { useCallback } from 'react';
import { ImpactAnalysisChatPayload } from '../../components/common/ai-chat/aiChatTypes';
import { AutomationVersionHistoryData } from '../../components/Automations/version-history/types';
import { AutoTemplatesStruct } from '../../constants/automationTemplates';

const automationTypeToPath = {
  [AutomationType.Alert]: '/automations/alerts',
  [AutomationType.Default]: '/automations',
  [AutomationType.Dedupe]: '/automations/dedupe-matching',
  [AutomationType.Matching]: '/automations/dedupe-matching',
  [AutomationType.Assignment]: '/automations/assignments',
  [AutomationType.ScheduledAssignment]: '/automations/assignments',
  [AutomationType.ScheduledReport]: '/automations/alerts',
  [AutomationType.ScheduledJob]: '/automations',
  [AutomationType.MarketingAttributionModel]: '/automations/marketing-attributions',
};

interface DeployAutomationDto {
  deployToOrgIds: string[];
  deployAs?: AutomationDeployAs;
}

export const useAutomationsApiFacade = () => {
  const sweepApi = useSweepApi();
  return {
    get_automation: useCallback(
      async (automationId: string, crmOrgId?: string, readOnly: boolean = false) => {
        const response = await sweepApi.get(
          `/automations/${automationId}?orgId=${crmOrgId}&includeReadOnly=${readOnly}`,
        );
        return response.data as AutomationStructureNew;
      },
      [sweepApi],
    ),
    get_automations: useCallback(async () => {
      const response = await sweepApi.get(`/automations`);
      return response.data as AutomationStructureNew[];
    }, [sweepApi]),
    create_automation: useCallback(
      async (createAutomationDto: AutomationStructureNew) => {
        const { type } = createAutomationDto;
        const endpoint = automationTypeToPath[type];
        const response = await sweepApi.post(endpoint, createAutomationDto);
        return response.data as AutomationStructureNew;
      },
      [sweepApi],
    ),
    update_automation: useCallback(
      async (automationDto: AutomationStructureNew) => {
        const { type } = automationDto;
        const endpoint = automationTypeToPath[type];
        const response = await sweepApi.put(
          `${endpoint}/${automationDto.automationId}/${automationDto.versionId}`,
          automationDto,
        );
        return response.data as AutomationStructureNew;
      },
      [sweepApi],
    ),
    toggle_automation: useCallback(
      async (toggleAutomationDto: AutomationStructureNew) => {
        const { type } = toggleAutomationDto;
        const endpoint = automationTypeToPath[type];
        await sweepApi.patch(
          `${endpoint}/activation/${toggleAutomationDto.automationId}/${
            toggleAutomationDto?.deployedVersionId ?? toggleAutomationDto.versionId
          }`,
          {
            deployToOrgIds: toggleAutomationDto.deployToOrgIds,
            isActive: toggleAutomationDto.isActive,
            deployAs: toggleAutomationDto.deployedAs,
          },
        );
      },
      [sweepApi],
    ),
    deploy_automation: useCallback(
      async (
        {
          automationId,
          versionId,
          type,
          deployToOrgIds,
          deployAs,
        }: {
          automationId: string;
          versionId: string;
          type: AutomationType;
          deployToOrgIds: string[];
          deployAs?: AutomationDeployAs;
        },
        // updateAutomationDto: AutomationStructureNew,
      ) => {
        // const { type } = updateAutomationDto;
        const deployAutomationDto: DeployAutomationDto = {
          deployToOrgIds,
          deployAs,
        };
        const endpoint = automationTypeToPath[type];
        await sweepApi.post(
          `${endpoint}/deployment/${automationId}/${versionId}`,
          deployAutomationDto,
        );
      },
      [sweepApi],
    ),
    delete_automation: useCallback(
      async (automationId: string, automationType: AutomationType) => {
        const endpoint = automationTypeToPath[automationType];
        await sweepApi.delete(`${endpoint}/${automationId}`);
      },
      [sweepApi],
    ),
    create_read_only_automation: useCallback(
      async (createAutomationDto: AutomationStructureNew) => {
        // this is until we remove the status in the DTO
        const _createAutomationDto = { ...createAutomationDto };
        if (createAutomationDto.status === DeployStatusForTable.Pending) {
          _createAutomationDto.status = DeployStatusForTable.Draft;
        }
        const endpoint = '/automations/read-only';
        const response = await sweepApi.post(endpoint, _createAutomationDto);
        return response.data as AutomationStructureNew;
      },
      [sweepApi],
    ),
    patch_automation_admin_notification: useCallback(
      async (automationId: string, versionId: string, note: string) => {
        await sweepApi.patch(`/automations/admin-notification/${automationId}/${versionId}`, {
          note,
        });
      },
      [sweepApi],
    ),
    patch_alerts_admin_notification: useCallback(
      async (automationId: string, versionId: string, note: string) => {
        await sweepApi.patch(
          `/automations/alerts/admin-notification/${automationId}/${versionId}`,
          {
            note,
          },
        );
      },
      [sweepApi],
    ),
    patch_assignments_admin_notification: useCallback(
      async (automationId: string, versionId: string, note: string) => {
        await sweepApi.patch(
          `/automations/assignments/admin-notification/${automationId}/${versionId}`,
          {
            note,
          },
        );
      },
      [sweepApi],
    ),
    patch_dedupe_matching_admin_notification: useCallback(
      async (automationId: string, versionId: string, note: string) => {
        await sweepApi.patch(
          `/automations/dedupe-matching/admin-notification/${automationId}/${versionId}`,
          {
            note,
          },
        );
      },
      [sweepApi],
    ),
    post_ai_impact_analysis_chat: useCallback(
      async (payload: ImpactAnalysisChatPayload) => {
        const { data } = await sweepApi.post<string>(
          '/automations/ai-impact-analysis-chat',
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    post_ai_create_automation: useCallback(
      async (payload: { description: string; crmOrgId: string }) => {
        const { data } = await sweepApi.post<AutoTemplatesStruct>(
          '/automations/ai-create',
          payload,
        );
        return data;
      },
      [sweepApi],
    ),
    get_automation_version_history: useCallback(
      async (automationId: string) => {
        const response = await sweepApi.get(`/automations/${automationId}/history`);
        return response.data as AutomationVersionHistoryData[];
      },
      [sweepApi],
    ),
    post_createMarketingAttributionTemplates: useCallback(
      async (_templates: Partial<AutomationStructureNew>[]) => {
        const response = await sweepApi.post(
          '/automations/marketing-attributions/templates',
          _templates,
        );
        return response.data as AutomationStructureNew[];
      },
      [sweepApi],
    ),
  };
};
